/**
 * The current organization's PureOA plan entitlements, for gating a designer control.
 *
 * WHY A HOOK AND NOT A PROP (2026-08-31)
 * --------------------------------------
 * `Plan.max_approval_levels` and `Plan.process_escalation` (svdy-platform alembic 117) are
 * enforced at publish, and a control that lets an admin configure something publish will then
 * refuse is the `multi_branch_enabled` mistake again: for two years that toggle promised
 * multi-branch and the Add Branch button answered with a 402. The switch has to know.
 *
 * Read from `GET /organization/profile`, which already mirrors every metering column — the same
 * source and the same lazy-fetch shape `TopNav` uses for `whitelabel`. No new endpoint.
 *
 * FAILS OPEN, deliberately and only here. A fetch that never lands leaves the switch ENABLED, so
 * the admin can still configure and the server still refuses with a 402 they can act on. The
 * alternative — a disabled control because a request failed — tells a paying Professional tenant
 * they do not have a feature they bought, which is the worse of the two wrong answers. The gate
 * that protects the product is the server's; this one only protects the admin's time.
 */
import { useEffect, useState } from 'react';

export interface ProcessEntitlements {
  /** Longest chain of approval steps a published process may have. 0 = no limit. */
  maxApprovalLevels: number;
  /** May an overdue approval be reassigned automatically (Professional+). */
  escalation: boolean;
  /** False until the profile lands. Callers must not disable anything while true. */
  loading: boolean;
}

export function useProcessEntitlements(): ProcessEntitlements {
  const [state, setState] = useState<ProcessEntitlements>({
    maxApprovalLevels: 0,
    escalation: true,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    import('../api/organization').then(({ profileApi }) => {
      profileApi.get()
        .then((profile) => {
          if (cancelled) return;
          setState({
            maxApprovalLevels: Number(profile.max_approval_levels ?? 0),
            escalation: Boolean(profile.process_escalation),
            loading: false,
          });
        })
        .catch(() => {
          // Leave the permissive defaults and stop loading, so a control that waits on
          // `loading` does not stay disabled forever on a network blip.
          if (!cancelled) setState((prev) => ({ ...prev, loading: false }));
        });
    });
    return () => { cancelled = true; };
  }, []);

  return state;
}
